import React, { useState } from 'react';
import Hero from '../../components/Hero/Hero';
import Pathfinder from '../../components/Pathfinder/Pathfinder';
import Section from '../../components/Section/Section';
import Card from '../../components/Card/Card';
import GlobalCallout from '../../components/Global/CalloutDefault';
import Wallpaper from '../../components/Wallpaper/Wallpaper';
import { Box, Typography } from '@mui/material';
import Accordion from '../../components/Accordion/Accordion';
import Metadata from '../../components/Metadata/Metadata';
import Button from '../../components/Button/Button';
import Image from '../../components/Image/Image';
import ContentSplit from '../../components/ContentSplit/ContentSplit';
import ListItem from '../../components/ListItem/ListItem';
import { Link } from 'react-router-dom';

const PageContent: React.FC = () => {
  const [expandAll, setExpandAll] = useState(false);

  const toggleAll = () => {
    setExpandAll(!expandAll);
  };

  return (
    <Box>
      <Metadata
        title="Explore General Genomics’ Capabilities & Innovations"
        description="Discover Tailored AI/ML Innovations"
      />
      <Hero
        helper="Our capabilities"
        headline="General Genomics’ core strengths in AI and analytics"
      />

      <Section theme="" className="cards-large-icons">
        <h2 className="action-on-view action-fade-in">
          Discover Tailored Innovations
        </h2>
        <p className="action-on-view action-fade-in">
          Experience the power of cutting-edge bioscience and AI solutions
          designed to transform your organization’s challenges into
          opportunities. General Genomics’ unparalleled expertise in predictive
          analytics, data management, and custom AI ensures tailored, impactful
          outcomes. Contact us today to explore how our innovative approach can
          deliver measurable results and meet your unique needs.
        </p>
        <Image
          path="/images/library/office-startup-8.webp"
          size="wide"
          className="action-on-view action-fade-in"
          alt="This is a wide image."
        />
      </Section>

      <ContentSplit
        image="/images/library/military-mechanic-man-repair-5.webp"
        position="right"
      >
        <p className="helper action-on-view action-fade-in">See the Benefits</p>
        <h2 className="action-on-view action-fade-in">Why partner with us</h2>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Unparalleled Expertise:</strong> Discover how General
            Genomics leverages AI and bioscience to deliver data-driven,
            tailored solutions for diverse industries.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Real-World Impact:</strong> See proven examples of our
            solutions improving clinical research, data management, and
            decision-making for clients worldwide.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Custom Solutions:</strong> Explore how we develop and
            implement strategies tailored to your organization’s specific
            challenges and objectives.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Future-Ready Technology:</strong> Learn about our innovative
            advancements, including predictive analytics and synthetic data
            (SYN46™), designed for tomorrow’s challenges.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Hands-On Experience:</strong> Understand the value of our
            solutions through a personalized, interactive demonstration tailored
            to your use case.
          </p>
        </ListItem>
      </ContentSplit>

      <Wallpaper asset="../images/library/government-building-data-2.webp"></Wallpaper>

      <Section>
        <Typography variant="h2" className="action-on-view action-fade-in">
          Frequently asked questions about General Genomics’ capabilities
        </Typography>
        <Typography className="action-on-view action-fade-in">
          Learn about General Genomics’ expertise in AI, bioscience, man-made
          systems, and data-driven solutions, including services, innovations,
          and real-world applications.
        </Typography>
        <Box className="accordion-wrapper action-on-view action-fade-in">
          <Button
            className="accordion-button"
            type="outlined"
            onClick={toggleAll}
            icon={expandAll ? 'expand_less' : 'expand_more'}
            label={expandAll ? 'Collapse All' : 'Expand All'}
          />
          <Accordion
            isOpen={expandAll}
            headline="What services does General Genomics offer?"
          >
            General Genomics provides data management, clinical research
            support, predictive analytics, bioinformatics, and custom AI/ML
            solutions tailored to your needs.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How does General Genomics use AI and bioscience to drive innovation?"
          >
            By combining cutting-edge AI/ML technologies with bioscience
            expertise, General Genomics develops data-driven solutions that
            deliver impactful, real-world results.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="What industries does General Genomics serve?"
          >
            General Genomics supports both{' '}
            <Link to="/industries/government">government</Link> and and{' '}
            <Link to="/industries/commercial">commercial</Link> sectors,
            addressing challenges in healthcare, biotechnology, and beyond with
            advanced solutions.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="Can you provide examples of General Genomics' successful projects?"
          >
            Yes! Our case studies highlight how we’ve used innovative approaches
            like synthetic data (SYN46™) and{' '}
            <Link to="/innovation/curo46">Curo46™</Link> to achieve
            transformative outcomes.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How can I learn more about solutions tailored to my organization?"
          >
            You can contact our team to learn more about how General Genomics’
            expertise in{' '}
            <Link to="/industries/government/bioinformatics">
              bioinformatics
            </Link>{' '}
            and synthetic data (SYN46™) aligns with your specific goals.
          </Accordion>
        </Box>
      </Section>

      <Section theme="dark" bgName="lines" className="section-call-to-action">
        <Typography variant="h2" className="action-on-view action-fade-in">
          Why partnering with General Genomics transforms bioinformatics
          research and innovation
        </Typography>
        <Box className="card-group">
          <Card
            icon="check"
            headline="Advanced Solutions in Analytics"
            subhead="General Genomics combines analytics and innovation with cutting-edge algorithms, large language models, machine learning, and predictive analytics to transform synthetic systems and complex data insights."
            className="action-on-view action-fade-in"
          />
          <Card
            icon="check"
            headline="Precision Insights"
            subhead="Harness predictive modeling and genomic data analysis to drive accurate, actionable insights tailored to research and healthcare objectives."
            className="action-on-view action-fade-in action-delay-250"
          />
          <Card
            icon="check"
            headline="Collaborative Expertise"
            subhead="Partner with a team experienced in supporting government initiatives to advance bioinformatics research and public health strategies."
            className="action-on-view action-fade-in action-delay-500"
          />
        </Box>
      </Section>

      <GlobalCallout />

      <Pathfinder />
    </Box>
  );
};

export default PageContent;
